import React, { useState, useCallback } from 'react';
import { TeacherAvatar } from '../../components/TeacherAvatar';
import { useTeacherAvatar } from '../../hooks/useTeacherAvatar';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DndContext, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, FlaskConical, RotateCcw, Star, Trash2, Check, X, Info, Zap, Target
} from 'lucide-react';

const ATOMS = {
  H: { symbol: 'H', name: 'Hydrogen', color: '#60a5fa', maxBonds: 1, electrons: 1, radius: 22 },
  O: { symbol: 'O', name: 'Oxygen', color: '#f87171', maxBonds: 2, electrons: 6, radius: 28 },
  C: { symbol: 'C', name: 'Carbon', color: '#4ade80', maxBonds: 4, electrons: 4, radius: 30 },
  N: { symbol: 'N', name: 'Nitrogen', color: '#a78bfa', maxBonds: 3, electrons: 5, radius: 28 },
  Cl: { symbol: 'Cl', name: 'Chlorine', color: '#fbbf24', maxBonds: 1, electrons: 7, radius: 32 },
  S: { symbol: 'S', name: 'Sulfur', color: '#fb923c', maxBonds: 2, electrons: 6, radius: 30 },
};

const MOLECULES = [
  { id: 'h2o', name: 'Water (H₂O)', formula: { H: 2, O: 1 }, hint: '2 Hydrogen + 1 Oxygen', stars: 1 },
  { id: 'co2', name: 'Carbon Dioxide (CO₂)', formula: { C: 1, O: 2 }, hint: '1 Carbon + 2 Oxygen', stars: 1 },
  { id: 'ch4', name: 'Methane (CH₄)', formula: { C: 1, H: 4 }, hint: '1 Carbon + 4 Hydrogen', stars: 2 },
  { id: 'nh3', name: 'Ammonia (NH₃)', formula: { N: 1, H: 3 }, hint: '1 Nitrogen + 3 Hydrogen', stars: 2 },
  { id: 'hcl', name: 'Hydrochloric Acid (HCl)', formula: { H: 1, Cl: 1 }, hint: '1 Hydrogen + 1 Chlorine', stars: 1 },
  { id: 'h2s', name: 'Hydrogen Sulfide (H₂S)', formula: { H: 2, S: 1 }, hint: '2 Hydrogen + 1 Sulfur — smells like rotten eggs', stars: 2 },
  { id: 'so2', name: 'Sulfur Dioxide (SO₂)', formula: { S: 1, O: 2 }, hint: '1 Sulfur + 2 Oxygen — causes acid rain', stars: 2 },
  { id: 'n2', name: 'Nitrogen Gas (N₂)', formula: { N: 2 }, hint: '2 Nitrogens — 78% of the air we breathe', stars: 2 },
  { id: 'o2', name: 'Oxygen Gas (O₂)', formula: { O: 2 }, hint: '2 Oxygens — what plants release', stars: 2 },
  { id: 'ch3cl', name: 'Chloromethane (CH₃Cl)', formula: { C: 1, H: 3, Cl: 1 }, hint: '1 Carbon + 3 Hydrogen + 1 Chlorine', stars: 3 },
];

// Draggable atom from palette
function PaletteAtom({ atomKey, atom }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${atomKey}`,
    data: { type: 'atom', atomKey },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`flex flex-col items-center gap-1 cursor-grab active:cursor-grabbing select-none ${isDragging ? 'opacity-30' : ''}`}
      data-testid={`palette-atom-${atomKey}`}
    >
      <motion.div
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        className="relative rounded-full flex items-center justify-center shadow-lg"
        style={{
          width: atom.radius * 2,
          height: atom.radius * 2,
          background: `radial-gradient(circle at 35% 35%, ${atom.color}dd, ${atom.color}88)`,
          boxShadow: `0 0 15px ${atom.color}40`,
        }}
      >
        <span className="text-white font-bold text-sm">{atom.symbol}</span>
      </motion.div>
      <span className="text-xs text-muted-foreground">{atom.name}</span>
      <span className="text-[10px] text-muted-foreground/60">bonds: {atom.maxBonds}</span>
    </div>
  );
}

// Workspace atom (placed)
function WorkspaceAtom({ atom, id, onRemove }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `workspace-${id}`,
    data: { type: 'workspace-atom', id },
  });

  const atomData = ATOMS[atom.atomKey];

  return (
    <motion.div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      initial={{ scale: 0 }}
      animate={{ scale: 1, opacity: isDragging ? 0.3 : 1 }}
      className="absolute cursor-grab active:cursor-grabbing group"
      style={{ left: atom.x - atomData.radius, top: atom.y - atomData.radius }}
    >
      <div
        className="rounded-full flex items-center justify-center relative"
        style={{
          width: atomData.radius * 2,
          height: atomData.radius * 2,
          background: `radial-gradient(circle at 35% 35%, ${atomData.color}dd, ${atomData.color}88)`,
          boxShadow: `0 0 20px ${atomData.color}50`,
        }}
      >
        <span className="text-white font-bold text-sm select-none">{atomData.symbol}</span>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(id); }}
          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`remove-atom-${id}`}
        >
          <X className="w-2.5 h-2.5" />
        </button>
      </div>
    </motion.div>
  );
}

function WorkspaceDropZone({ children }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'workspace' });

  return (
    <div
      ref={setNodeRef}
      className={`relative w-full h-[400px] rounded-2xl border-2 border-dashed transition-colors ${
        isOver ? 'border-primary/60 bg-primary/5' : 'border-border/50 bg-card/50'
      }`}
      data-testid="molecule-workspace"
    >
      {children}
      {!children || (Array.isArray(children) && children.filter(Boolean).length <= 1) ? (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <p className="text-muted-foreground/40 text-sm">Drag atoms here to build a molecule</p>
        </div>
      ) : null}
    </div>
  );
}

export default function ChemistryLab() {
  const { showTeacher, dismiss, resummon, dismissed, avatarId } = useTeacherAvatar('chemistry');
  const navigate = useNavigate();
  const [workspaceAtoms, setWorkspaceAtoms] = useState([]);
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('chemistry', 10);
  const [activeDragAtom, setActiveDragAtom] = useState(null);
  const [result, setResult] = useState(null);
  const [nextId, setNextId] = useState(0);

  const mission = MOLECULES[currentMission];

  const handleDragStart = (event) => {
    const { active } = event;
    if (active.data.current?.type === 'atom') {
      setActiveDragAtom(active.data.current.atomKey);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragAtom(null);

    if (over?.id === 'workspace' && active.data.current?.type === 'atom') {
      const atomKey = active.data.current.atomKey;
      // Random position in workspace
      const x = 80 + Math.random() * 400;
      const y = 80 + Math.random() * 240;
      setWorkspaceAtoms(prev => [...prev, { id: nextId, atomKey, x, y }]);
      setNextId(prev => prev + 1);
      setResult(null);
    }
  };

  const removeAtom = useCallback((id) => {
    setWorkspaceAtoms(prev => prev.filter(a => a.id !== id));
    setResult(null);
  }, []);

  const checkMolecule = () => {
    // Count atoms in workspace
    const counts = {};
    workspaceAtoms.forEach(a => {
      counts[a.atomKey] = (counts[a.atomKey] || 0) + 1;
    });

    // Compare with target formula
    const target = mission.formula;
    const isCorrect = Object.keys(target).every(k => counts[k] === target[k]) &&
                      Object.keys(counts).every(k => target[k] === counts[k]);

    if (isCorrect) {
      setResult('correct');
      if (!completedMissions.includes(currentMission)) {
        saveProgress(currentMission);
      }
    } else {
      setResult('incorrect');
    }
  };

  const clearWorkspace = () => {
    setWorkspaceAtoms([]);
    setResult(null);
  };

  // Draw bonds between nearby atoms
  const bonds = [];
  for (let i = 0; i < workspaceAtoms.length; i++) {
    for (let j = i + 1; j < workspaceAtoms.length; j++) {
      const a = workspaceAtoms[i];
      const b = workspaceAtoms[j];
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 120) {
        bonds.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y, key: `${a.id}-${b.id}` });
      }
    }
  }

  return (
    <div className="min-h-screen bg-background" data-testid="chemistry-lab">
      <TeacherAvatar visible={showTeacher} avatarId={avatarId}
        text="Chemistry lab about atoms, bonds, and molecules. Students drag atoms and connect them to build real molecules and learn chemical bonding."
        cacheKey="lab-intro-chemistry"
        onClose={dismiss} dismissed={dismissed} onResummon={resummon} />
      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/labs')} data-testid="back-to-labs">
              <ArrowLeft className="w-4 h-4 mr-2" /> Labs
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-emerald-400" />
              <span className="font-semibold">Molecule Builder</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {MOLECULES.map((m, i) => (
              <Badge
                key={m.id}
                variant={completedMissions.includes(i) ? 'default' : currentMission === i ? 'secondary' : 'outline'}
                className={`cursor-pointer text-xs ${currentMission === i ? 'ring-2 ring-primary/50' : ''}`}
                onClick={() => { clearWorkspace(); setCurrentMission(i); }}
                data-testid={`mission-${m.id}`}
              >
                {completedMissions.includes(i) ? <Star className="w-3 h-3 mr-1 fill-yellow-400 text-yellow-400" /> : null}
                {m.name.split('(')[0].trim()}
              </Badge>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Mission Info */}
        <motion.div
          key={currentMission}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20"
        >
          <Target className="w-5 h-5 text-emerald-400 shrink-0" />
          <div>
            <span className="font-semibold text-sm">Build: {mission.name}</span>
            <span className="text-sm text-muted-foreground ml-2">({mission.hint})</span>
          </div>
          <div className="ml-auto flex gap-1">
            {[...Array(mission.stars)].map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${completedMissions.includes(currentMission) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
            ))}
          </div>
        </motion.div>

        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Workspace */}
            <div className="lg:col-span-3">
              <WorkspaceDropZone>
                {/* Bond lines */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none">
                  {bonds.map(b => (
                    <line
                      key={b.key}
                      x1={b.x1} y1={b.y1} x2={b.x2} y2={b.y2}
                      stroke="rgba(148,163,184,0.5)"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  ))}
                </svg>
                {workspaceAtoms.map(atom => (
                  <WorkspaceAtom
                    key={atom.id}
                    id={atom.id}
                    atom={atom}
                    onRemove={removeAtom}
                  />
                ))}
              </WorkspaceDropZone>

              {/* Result & actions */}
              <div className="flex items-center gap-3 mt-4">
                <Button onClick={checkMolecule} disabled={workspaceAtoms.length === 0} data-testid="check-molecule-btn">
                  <Check className="w-4 h-4 mr-2" /> Check Molecule
                </Button>
                <Button variant="outline" onClick={clearWorkspace} data-testid="clear-workspace-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Clear
                </Button>

                <AnimatePresence>
                  {result === 'correct' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30"
                    >
                      <Check className="w-4 h-4" />
                      <span className="text-sm font-medium">Correct! You built {mission.name}</span>
                    </motion.div>
                  )}
                  {result === 'incorrect' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30"
                    >
                      <X className="w-4 h-4" />
                      <span className="text-sm font-medium">Not quite right. Check the formula!</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Atom Palette */}
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-card border border-border/50">
                <h4 className="text-sm font-semibold mb-4 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-400" /> Atom Palette
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(ATOMS).map(([key, atom]) => (
                    <PaletteAtom key={key} atomKey={key} atom={atom} />
                  ))}
                </div>
              </div>

              {/* Current formula count */}
              <div className="p-4 rounded-xl bg-card border border-border/50">
                <h4 className="text-sm font-semibold mb-3">Your Atoms</h4>
                <div className="space-y-2">
                  {Object.entries(
                    workspaceAtoms.reduce((acc, a) => {
                      acc[a.atomKey] = (acc[a.atomKey] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([key, count]) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ background: ATOMS[key].color }}
                        />
                        <span>{ATOMS[key].name}</span>
                      </div>
                      <span className="font-mono">{count}</span>
                    </div>
                  ))}
                  {workspaceAtoms.length === 0 && (
                    <p className="text-xs text-muted-foreground">No atoms placed yet</p>
                  )}
                </div>
              </div>

              {/* Target */}
              <div className="p-4 rounded-xl bg-card border border-border/50">
                <h4 className="text-sm font-semibold mb-3">Target Formula</h4>
                <div className="space-y-2">
                  {Object.entries(mission.formula).map(([key, count]) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ background: ATOMS[key].color }}
                        />
                        <span>{ATOMS[key].name}</span>
                      </div>
                      <span className="font-mono">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Info */}
              <div className="p-4 rounded-xl bg-muted/30 border border-border/30">
                <h4 className="text-xs font-semibold flex items-center gap-1 mb-2 text-muted-foreground">
                  <Info className="w-3 h-3" /> Chemistry
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Atoms bond together following valency rules. Hydrogen forms 1 bond,
                  Oxygen forms 2, Nitrogen forms 3, and Carbon forms 4. Drag the right
                  atoms to build the target molecule!
                </p>
              </div>
            </div>
          </div>

          <DragOverlay>
            {activeDragAtom ? (
              <div
                className="rounded-full flex items-center justify-center opacity-80"
                style={{
                  width: ATOMS[activeDragAtom].radius * 2,
                  height: ATOMS[activeDragAtom].radius * 2,
                  background: `radial-gradient(circle at 35% 35%, ${ATOMS[activeDragAtom].color}dd, ${ATOMS[activeDragAtom].color}88)`,
                  boxShadow: `0 0 25px ${ATOMS[activeDragAtom].color}60`,
                }}
              >
                <span className="text-white font-bold text-sm">{ATOMS[activeDragAtom].symbol}</span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}
